<template>
  <div class="container-fluid">
    <div class="page-title">
      <div class="row">
        <div class="col-6">
          <h3>Dashboard de pagos</h3>
        </div>
        <div class="col-6 text-end">
          <button class="btn btn-outline-primary" :disabled="loading" @click="loadAll">
            {{ loading ? 'Cargando...' : 'Refrescar' }}
          </button>
        </div>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label class="form-label">Fecha inicio</label>
            <input v-model="filters.startDate" type="date" class="form-control" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Fecha fin</label>
            <input v-model="filters.endDate" type="date" class="form-control" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Agrupar por período</label>
            <select v-model="filters.period" class="form-select">
              <option value="day">Día</option>
              <option value="week">Semana</option>
              <option value="month">Mes</option>
              <option value="year">Año</option>
            </select>
          </div>
          <div class="col-12 text-end">
            <button class="btn btn-primary me-2" :disabled="loading" @click="loadAll">Aplicar filtros</button>
            <button class="btn btn-outline-danger" :disabled="loading" @click="resetFilters">Limpiar</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="errorMsg" class="alert alert-danger">{{ errorMsg }}</div>

    <div class="row g-3 mb-3">
      <div class="col-md-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="small text-muted">Total de pagos</div>
            <div class="h4 mb-0">{{ stats.totalPayments }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="small text-muted">Monto total</div>
            <div class="h4 mb-0">{{ formatMxn(stats.totalAmount) }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="small text-muted">Reembolsos</div>
            <div class="h4 mb-0">{{ stats.totalRefunded }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="small text-muted">Monto reembolsado</div>
            <div class="h4 mb-0">{{ formatMxn(stats.totalRefundedAmount) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-lg-6">
        <div class="card">
          <div class="card-header pb-0"><h5>Desglose por estado</h5></div>
          <div class="card-body">
            <div v-if="loading" class="text-muted">Cargando...</div>
            <div v-else-if="statusStats.length === 0" class="text-muted">Sin datos.</div>
            <div v-else class="table-responsive">
              <table class="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Cantidad</th>
                    <th>Monto</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in statusStats" :key="row.status">
                    <td><span class="badge" :class="statusBadgeClass(row.status)">{{ statusLabel(row.status) }}</span></td>
                    <td>{{ row.count }}</td>
                    <td>{{ formatMxn(row.amount) }}</td>
                    <td>{{ Number(row.percentage || 0).toFixed(2) }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="card">
          <div class="card-header pb-0"><h5>Evolución por período</h5></div>
          <div class="card-body">
            <div v-if="loading" class="text-muted">Cargando...</div>
            <div v-else-if="periodStats.length === 0" class="text-muted">Sin datos.</div>
            <div v-else class="table-responsive">
              <table class="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Período</th>
                    <th>Cantidad</th>
                    <th>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in periodStats" :key="row.period">
                    <td>{{ row.period }}</td>
                    <td>{{ row.count }}</td>
                    <td>{{ formatMxn(row.amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { paymentsApi } from '@/api/payments';

export default {
  name: 'DashboardPagos',
  data() {
    return {
      loading: false,
      errorMsg: '',
      filters: {
        startDate: '',
        endDate: '',
        period: 'day',
      },
      stats: {
        totalPayments: 0,
        totalAmount: 0,
        totalRefunded: 0,
        totalRefundedAmount: 0,
      },
      statusStats: [],
      periodStats: [],
    };
  },
  mounted() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    this.filters.endDate = end.toISOString().split('T')[0];
    this.filters.startDate = start.toISOString().split('T')[0];
    this.loadAll();
  },
  methods: {
    buildParams() {
      const params = {};
      if (this.filters.startDate) params.startDate = this.filters.startDate;
      if (this.filters.endDate) params.endDate = this.filters.endDate;
      return params;
    },
    async loadAll() {
      this.loading = true;
      this.errorMsg = '';
      try {
        const baseParams = this.buildParams();
        const [statsRes, byStatusRes, byPeriodRes] = await Promise.all([
          paymentsApi.getStats(baseParams),
          paymentsApi.getStatsByStatus(baseParams),
          paymentsApi.getStatsByPeriod({
            ...baseParams,
            period: this.filters.period,
          }),
        ]);

        this.stats = statsRes.data || this.stats;
        this.statusStats = byStatusRes.data || [];
        this.periodStats = byPeriodRes.data || [];
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'No se pudo cargar el dashboard de pagos.';
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters.startDate = '';
      this.filters.endDate = '';
      this.filters.period = 'day';
      this.loadAll();
    },
    statusLabel(status) {
      return {
        pending: 'Pendiente',
        succeeded: 'Exitoso',
        failed: 'Fallido',
        canceled: 'Cancelado',
        refunded: 'Reembolsado',
        partially_refunded: 'Parcialmente reembolsado',
      }[status] || status;
    },
    statusBadgeClass(status) {
      return {
        pending: 'bg-warning text-dark',
        succeeded: 'bg-success',
        failed: 'bg-danger',
        canceled: 'bg-secondary',
        refunded: 'bg-danger',
        partially_refunded: 'bg-info text-dark',
      }[status] || 'bg-secondary';
    },
    formatMxn(amount) {
      const n = Number(amount) || 0;
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(n);
    },
  },
};
</script>

